import { SelectedOrderProductData } from "@/types/notifications";
import { supabase } from "@/integrations/supabase/client";

interface PackersLineItem {
  sku: string;
  producttitle: string;
  varianttitle?: string;
  base_paint?: {
    product_naam: string;
    booking_product?: {
      product_id: string;
      product_naam: string | null;
    } | null;
  } | null;
}

interface PackersOrder {
  line_items: PackersLineItem[];
}

/**
 * Enriches products with base paint names by fetching from the packers API
 * and matching on SKU.
 */
export async function enrichWithBasePaint(
  voNumber: string,
  products: SelectedOrderProductData[]
): Promise<SelectedOrderProductData[]> {
  if (!voNumber || products.length === 0) return products;

  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const sessionToken = localStorage.getItem('auth_session_token');
    const proxyUrl = `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;

    const params = new URLSearchParams({ action: 'packers_list', search: voNumber });
    const res = await fetch(`${proxyUrl}?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-session-token': sessionToken || '',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
    });

    if (!res.ok) return products;

    const json = await res.json();
    if (!json.success || !json.data) return products;

    const orders = json.data as PackersOrder[];
    const basePaintMap = new Map<string, { name: string; sku: string }>();

    for (const order of orders) {
      for (const li of order.line_items) {
        if (li.base_paint?.product_naam && li.sku) {
          const basePaintSku = li.base_paint?.booking_product?.product_id || '';
          basePaintMap.set(li.sku, {
            name: li.base_paint.product_naam,
            sku: basePaintSku,
          });
        }
      }
    }

    // Lookup missing SKUs via voorraad_live
    const missingSkuEntries = [...basePaintMap.entries()]
      .filter(([_, v]) => v.name && !v.sku);

    if (missingSkuEntries.length > 0) {
      const names = missingSkuEntries.map(([_, v]) => v.name);
      console.log('[basePaintEnrichment] Looking up SKUs for base paint names:', names);
      const orFilter = names.map(n => `product_naam.ilike.${n}`).join(',');
      const { data } = await supabase
        .from('voorraad_live')
        .select('product_id, product_naam')
        .or(orFilter);
      console.log('[basePaintEnrichment] voorraad_live results:', data);

      if (data) {
        const nameToId = new Map(data.map(r => [r.product_naam.toLowerCase(), r.product_id]));
        for (const [sku, entry] of missingSkuEntries) {
          const foundId = nameToId.get(entry.name.toLowerCase());
          if (foundId) {
            basePaintMap.set(sku, { ...entry, sku: foundId });
          }
        }
      }
    }

    if (basePaintMap.size === 0) return products;

    return products.map(p => {
      const cached = p.sku ? basePaintMap.get(p.sku) : undefined;
      return {
        ...p,
        basePaintName: p.basePaintName || cached?.name || null,
        basePaintSku: p.basePaintSku || cached?.sku || null,
      };
    });
  } catch (err) {
    console.error("Error fetching base paint info:", err);
    return products;
  }
}
